// computeActivityJoinAmountMinor — the SINGLE source for an activity-join charge amount (öre).
//
// 3f 2d-i: all three participation paths (join via detail page, join via list, accept invitation)
// call this to derive p_amount_minor before invoking join_activity_v2 / accept_activity_invitation.
// It CONSUMES calculateUniversalPrice (court share, member category + daily-limit resolved there)
// and composes the fee-led total via composeJoinPriceMinor (Lesson 38: composition, not duplication).
//
// Display/i18n M1: computeActivityJoinBreakdownMinor exposes the FULL JoinPriceResult
// {courtShareMinor, feeMinor, totalMinor} so every price display (card, confirm-dialog rows,
// detail page) renders from the SAME computation that charges — court + fee = total by
// construction (composeJoinPriceMinor), integer öre. The legacy number-returning helper is a
// thin wrapper over the breakdown.
//
// Only system-mode activities charge in-app. external/free → zero (no charge, no payment row).
// NO LINKED BOOKING → zero too: _create_activity_join_payment refuses to create a payment row
// without a booking to hang it on (migration 20260608120000:32-34, booking_payments.booking_id
// NOT NULL), so a court-less activity genuinely charges nothing — the helper is true to what is
// actually charged, NOT to a hypothetical court price. (Whether court-less system activities
// SHOULD charge is an open product decision — tracked in todo.md, not decided here.)
// The RPC re-gates on payment_mode + total>0 server-side regardless.

import { supabase } from "@/integrations/supabase/client";
import {
  calculateUniversalPrice,
  type ActivityPricing,
  type CategoryConfig,
  type PricingRule,
} from "@/lib/playerPricing";
import { calculateBookingPrice } from "@/lib/pricing";
import { composeJoinPriceMinor, type JoinPriceResult } from "@/lib/composeJoinPriceMinor";

// Genuinely-no-charge breakdown (non-system mode, or no linked booking). totalMinor 0 — callers
// render the free/0-presentation and the join creates no payment row.
const ZERO_BREAKDOWN: JoinPriceResult = Object.freeze({
  totalMinor: 0,
  courtShareMinor: 0,
  feeMinor: 0,
  usedStandardCourt: false,
});

export interface JoinAmountActivity {
  id: string;
  payment_mode: string | null;
  spots_total: number | null;
  organizer_fee_per_person_minor: number | null;
  apply_member_discount: boolean | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
}

export interface ComputeJoinAmountArgs {
  activity: JoinAmountActivity;
  userId: string | null | undefined;
  orgId: string | null | undefined;
  hallId: string | null | undefined;
  pricingRules: PricingRule[];
  categories: Array<{
    slug: string;
    name: string;
    daily_minutes_limit?: number | null;
    daily_limit_fallback_category?: string | null;
    overflow_percent?: number | null;
  }>;
  /**
   * Does a bookings row exist for this activity? Mirrors the charge's actual gate:
   * _create_activity_join_payment returns null (no payment row, no charge) when the activity has
   * no booking. false → ZERO breakdown, regardless of what a court recompute would say.
   */
  hasLinkedBooking: boolean;
}

/**
 * Full canonical join-charge breakdown for a system-mode activity, in öre:
 *   courtShareMinor (category/daily-limit resolved; apply_member_discount=false forces standard)
 * + feeMinor        (organizer_fee_per_person_minor, never discounted)
 * = totalMinor      (by construction — composeJoinPriceMinor sums the same integers)
 * Zero breakdown for non-system modes and for activities without a linked booking.
 */
export async function computeActivityJoinBreakdownMinor(
  args: ComputeJoinAmountArgs,
): Promise<JoinPriceResult> {
  const { activity, userId, orgId, hallId, pricingRules, categories, hasLinkedBooking } = args;

  // Only system-mode charges in-app.
  if (activity.payment_mode !== "system") return ZERO_BREAKDOWN;

  // No booking → _create_activity_join_payment creates no payment row → nothing is charged.
  if (!hasLinkedBooking) return ZERO_BREAKDOWN;

  const spots = Math.max(1, activity.spots_total ?? 4);
  const feeMinor = activity.organizer_fee_per_person_minor ?? 0;
  const applyMemberDiscount = !!activity.apply_member_discount;

  // Court rental is RECOMPUTED from pricing_rules. The v2 blocking booking is stored at price 0,
  // so booking.price_total MUST NOT be used (it dropped the court share for non-members → only the
  // fee was charged, mismatching the displayed price). Displays consume THIS breakdown (M1).
  if (!userId || !orgId || !hallId || !activity.date || !activity.start_time || !activity.end_time) {
    // Can't recompute court without slot/hall — charge the fee only (degraded; not expected for system mode).
    return composeJoinPriceMinor({
      resolvedCourtShareKr: 0,
      standardCourtShareKr: 0,
      applyMemberDiscount,
      organizerFeeMinor: feeMinor,
    });
  }

  const startDt = new Date(`${activity.date}T${activity.start_time}`);
  const endDt = new Date(`${activity.date}T${activity.end_time}`);
  const durMin = Math.round((endDt.getTime() - startDt.getTime()) / 60000);

  // Standard court rate (whole court for the slot) / players, ROUNDED to whole krona to mirror
  // calculateUniversalPrice's own standard-category formula (round(total / participants)). Used
  // only when the organizer disables the member discount (apply_member_discount = false).
  const standardTotalKr = calculateBookingPrice(pricingRules, hallId, startDt, durMin, "standard").total;
  const standardCourtShareKr = spots > 0 ? Math.round(standardTotalKr / spots) : 0;

  const catConfig: CategoryConfig[] = categories.map((c) => ({
    slug: c.slug,
    name: c.name,
    daily_minutes_limit: c.daily_minutes_limit ?? null,
    daily_limit_fallback_category: c.daily_limit_fallback_category ?? "standard",
    overflow_percent: c.overflow_percent ?? 100,
  }));

  const activityPricing: ActivityPricing = {
    courtCostTotal: standardTotalKr,
    courtParticipants: spots,
    courtDurationMinutes: durMin,
    serviceCostTotal: 0,
    serviceParticipants: spots,
  };

  // calculateUniversalPrice resolves the player's member category + daily-limit; courtShare is the
  // player's resolved-category court share (member-discounted if applicable), recomputed from rules.
  const pr = await calculateUniversalPrice(
    userId,
    orgId,
    hallId,
    startDt,
    activityPricing,
    pricingRules,
    [],
    catConfig,
  );

  // The charge court reads EXACTLY calculateUniversalPrice's resolved court share (pr.courtShare) —
  // the SAME value every display renders via this breakdown. apply_member_discount picks
  // resolved (member) vs standard. Guarantees display == charge by construction.
  return composeJoinPriceMinor({
    resolvedCourtShareKr: pr.courtShare,
    standardCourtShareKr,
    applyMemberDiscount,
    organizerFeeMinor: feeMinor,
  });
}

/**
 * Returns the öre the joining player owes (the breakdown's totalMinor). 0 → no charge.
 * Thin wrapper over computeActivityJoinBreakdownMinor — same computation, same gates.
 */
export async function computeActivityJoinAmountMinor(args: ComputeJoinAmountArgs): Promise<number> {
  return (await computeActivityJoinBreakdownMinor(args)).totalMinor;
}

/**
 * Self-contained variant for callers without org/hall hook context (e.g. the public
 * invitation-accept page): fetches the activity + its hall + pricing rules + categories
 * (scoped to the ACTIVITY's org, not the caller's active org), then delegates to
 * computeActivityJoinBreakdownMinor. Zero breakdown for non-system mode, unreadable data,
 * or no linked booking (mirrors the charge gate).
 */
export async function computeActivityJoinBreakdownMinorById(
  activityId: string,
  userId: string | null | undefined,
): Promise<JoinPriceResult> {
  const { data: act } = await supabase
    .from("activities")
    .select(
      "id, organization_id, court_id, payment_mode, spots_total, organizer_fee_per_person_minor, apply_member_discount, date, start_time, end_time",
    )
    .eq("id", activityId)
    .maybeSingle();
  if (!act) return ZERO_BREAKDOWN;
  if (act.payment_mode !== "system") return ZERO_BREAKDOWN;

  // The charge gate: does a booking exist for this activity? (same lookup _create_activity_join_payment does)
  const { data: linkedBooking } = await supabase
    .from("bookings")
    .select("id, court_id")
    .eq("activity_id", activityId)
    .limit(1)
    .maybeSingle();

  // Hall derivation: activities.court_id is NULL for every MULTI-COURT activity
  // (create_activity_with_bookings: v_single only set for exactly 1 court) — deriving the hall
  // from it alone made the invite path silently compute FEE-ONLY (the degraded branch) for
  // activities the join path prices in full. The linked BOOKING's court (NOT NULL on bookings)
  // is the charge's own anchor and always resolves when a booking exists.
  const hallCourtId = act.court_id ?? linkedBooking?.court_id ?? null;
  let hallId: string | null = null;
  if (hallCourtId) {
    const { data: court } = await supabase
      .from("courts")
      .select("hall_id")
      .eq("id", hallCourtId)
      .maybeSingle();
    hallId = court?.hall_id ?? null;
  }

  const { data: rules } = hallId
    ? await supabase.from("pricing_rules").select("*").eq("hall_id", hallId)
    : { data: [] as PricingRule[] };

  const { data: cats } = await supabase
    .from("customer_categories")
    .select("slug, name, daily_minutes_limit, daily_limit_fallback_category, overflow_percent")
    .eq("organization_id", act.organization_id);

  return computeActivityJoinBreakdownMinor({
    activity: act,
    userId,
    orgId: act.organization_id,
    hallId,
    pricingRules: (rules ?? []) as PricingRule[],
    categories: cats ?? [],
    hasLinkedBooking: !!linkedBooking,
  });
}

/** Number-returning ById variant (the breakdown's totalMinor). */
export async function computeActivityJoinAmountMinorById(
  activityId: string,
  userId: string | null | undefined,
): Promise<number> {
  return (await computeActivityJoinBreakdownMinorById(activityId, userId)).totalMinor;
}
